import { Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { AdminLayout, CustomerLayout } from "./components/Layout";
import { ProtectedRoute } from "./components/ProtectedRoute";

import { LandingPage } from "./pages/LandingPage";
import { LoginPage } from "./pages/LoginPage";
import { RegisterPage } from "./pages/RegisterPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { GuestGalleryPage } from "./pages/GuestGalleryPage";

import { HearingPage } from "./pages/HearingPage";
import { InitialPaymentPage } from "./pages/InitialPaymentPage";
import { UploadsPage } from "./pages/UploadsPage";
import { ProfileImagePage } from "./pages/ProfileImagePage";
import { OptionsPage } from "./pages/OptionsPage";
import { GeneratingPage } from "./pages/GeneratingPage";
import { DesignSelectPage } from "./pages/DesignSelectPage";
import { DesignTunePage } from "./pages/DesignTunePage";
import { SitemapPage } from "./pages/SitemapPage";
import { DomainGuidePage } from "./pages/DomainGuidePage";
import { DashboardPage } from "./pages/DashboardPage";
import { BlogStudioPage } from "./pages/BlogStudioPage";
import { ContactInboxPage } from "./pages/ContactInboxPage";
import { KeywordStrategyPage } from "./pages/KeywordStrategyPage";
import { PagePlanPage } from "./pages/PagePlanPage";
import { AssetsPage } from "./pages/AssetsPage";

import { AdminCasesPage } from "./pages/admin/AdminCasesPage";
import { AdminCaseDetailPage } from "./pages/admin/AdminCaseDetailPage";
import { AdminDeployPage } from "./pages/admin/AdminDeployPage";
import { AdminBillingPage } from "./pages/admin/AdminBillingPage";
import { AdminCreditsPage } from "./pages/admin/AdminCreditsPage";

import { TokushohoPage } from "./pages/legal/TokushohoPage";
import { TermsPage } from "./pages/legal/TermsPage";
import { PrivacyPage } from "./pages/legal/PrivacyPage";

export function App() {
  const [creditShortage, setCreditShortage] = useState<null | {
    message: string;
    required: number;
    balance: number;
    feature: string;
  }>(null);

  useEffect(() => {
    const onShortage = (event: Event) => {
      const detail = (event as CustomEvent).detail as typeof creditShortage;
      if (detail) setCreditShortage(detail);
    };
    window.addEventListener("legalpages:credit-shortage", onShortage);
    return () => window.removeEventListener("legalpages:credit-shortage", onShortage);
  }, []);

  return (
    <>
    {creditShortage && (
      <div className="lp-credit-shortage" role="dialog" aria-modal="true" aria-labelledby="credit-shortage-title">
        <div>
          <span className="lp-section-kicker">AI CREDIT REQUIRED</span>
          <h1 id="credit-shortage-title">クレジットが足りません</h1>
          <p>{creditShortage.message}</p>
          <dl>
            <div><dt>必要クレジット</dt><dd>{creditShortage.required}cr</dd></div>
            <div><dt>現在の残高</dt><dd>{creditShortage.balance}cr</dd></div>
          </dl>
          <div className="lp-modal-actions">
            <button className="btn btn-ghost" type="button" onClick={() => setCreditShortage(null)}>閉じる</button>
            <a className="btn btn-primary" href="/dashboard">クレジットを購入する</a>
          </div>
        </div>
      </div>
    )}
    <Routes>
      {/* 公開 */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* ゲスト営業用の公開ギャラリー(認証不要・共有リンク限定) */}
      <Route path="/g/:token" element={<GuestGalleryPage />} />
      <Route path="/tokushoho" element={<TokushohoPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />

      {/* 顧客エリア(要ログイン) */}
      <Route element={<ProtectedRoute />}>
        <Route element={<CustomerLayout />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/payment" element={<InitialPaymentPage />} />
          <Route path="/hearing" element={<HearingPage />} />
          <Route path="/keywords" element={<KeywordStrategyPage />} />
          <Route path="/page-plan" element={<PagePlanPage />} />
          <Route path="/assets" element={<AssetsPage />} />
          <Route path="/uploads" element={<UploadsPage />} />
          <Route path="/profile-image" element={<ProfileImagePage />} />
          <Route path="/options" element={<OptionsPage />} />
          <Route path="/generating" element={<GeneratingPage />} />
          <Route path="/design" element={<DesignSelectPage />} />
          <Route path="/design/tune" element={<DesignTunePage />} />
          <Route path="/sitemap" element={<SitemapPage />} />
          <Route path="/domain-guide" element={<DomainGuidePage />} />
          <Route path="/contacts" element={<ContactInboxPage />} />
          <Route path="/studio/blog" element={<BlogStudioPage />} />
        </Route>
      </Route>

      {/* 運営エリア(要 admin) */}
      <Route element={<ProtectedRoute adminOnly />}>
        <Route element={<AdminLayout />}>
          <Route path="/admin" element={<AdminCasesPage />} />
          <Route path="/admin/cases/:id" element={<AdminCaseDetailPage />} />
          <Route path="/admin/deploy" element={<AdminDeployPage />} />
          <Route path="/admin/billing" element={<AdminBillingPage />} />
          <Route path="/admin/credits" element={<AdminCreditsPage />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}
